import {
  Badge,
  ButtonUtility,
  Table,
  Tooltip,
  TooltipTrigger,
} from "@light-technology/components";
import { AlertTriangle, Edit05, Trash01 } from "@untitledui/icons";
import { api } from "../../state/api";
import { getPrimaryRole, ROLE_TO_STRING } from "../../utilities/role";
import { ConfirmRemoveModal } from "../shared/ConfirmRemoveModal";
import { TruncatedText } from "../shared/TruncatedText";
import { EditPermissionsModal } from "./EditPermissionsModal";

type InvitesListProps = {
  orgSlug: string;
};

export const InvitesList = ({ orgSlug }: InvitesListProps) => {
  const invitesQuery = api.useGetInvitesQuery(orgSlug);
  const [deleteInvite] = api.useDeleteInviteMutation();
  const [inviteMember, inviteMemberState] = api.useInviteMemberMutation();

  const invites = invitesQuery.data?.data ?? [];
  const pendingInvites = [...invites]
    .filter((invite) => !invite.is_expired)
    .sort((a, b) => a.email.toLowerCase().localeCompare(b.email.toLowerCase()));

  return pendingInvites.map((invite) => {
    const inviteRole = getPrimaryRole(invite.roles);
    const fullName = `${invite.first_name} ${invite.last_name}`.trim();

    return (
      <Table.Row key={invite.email}>
        <Table.Cell>
          <TruncatedText
            content={fullName}
            className="font-medium text-primary"
            badge={
              invite.error_message ? (
                <Tooltip
                  title={`We were unable to send an invite to ${invite.email}. Please check that the email is correct and try again.`}
                  placement="top"
                >
                  <TooltipTrigger>
                    <Badge
                      type="pill-color"
                      size="sm"
                      color="warning"
                      className="flex flex-shrink-0 items-center gap-1"
                    >
                      <AlertTriangle className="size-3" aria-label="Warning" />
                      Invited
                    </Badge>
                  </TooltipTrigger>
                </Tooltip>
              ) : (
                <Badge
                  type="pill-color"
                  size="sm"
                  color="gray"
                  className="flex-shrink-0"
                >
                  Invited
                </Badge>
              )
            }
          />
        </Table.Cell>
        <Table.Cell>
          <TruncatedText content={invite.email} className="text-tertiary" />
        </Table.Cell>
        <Table.Cell>{ROLE_TO_STRING[inviteRole]}</Table.Cell>
        <Table.Cell>
          <div className="flex items-center gap-2">
            <EditPermissionsModal
              displayName={fullName || invite.email}
              currentRole={inviteRole}
              isLoading={inviteMemberState.isLoading}
              isSuccess={inviteMemberState.isSuccess}
              isFetching={invitesQuery.isFetching}
              error={inviteMemberState.error}
              onSubmit={(role) => {
                inviteMember({
                  email: invite.email,
                  first_name: invite.first_name,
                  last_name: invite.last_name,
                  orgSlug,
                  roles: [role],
                });
              }}
              onReset={inviteMemberState.reset}
            >
              <ButtonUtility size="xs" color="tertiary" icon={Edit05} />
            </EditPermissionsModal>
            <ConfirmRemoveModal
              title={`Are you sure you want to remove the invite for ${fullName || invite.email}?`}
              description="They will no longer be able to join your team with this invite."
              onConfirm={() => {
                deleteInvite({ orgSlug, email: invite.email });
              }}
              data-ph-capture-attribute-action="remove-team-invite"
            >
              <ButtonUtility size="xs" color="tertiary" icon={Trash01} />
            </ConfirmRemoveModal>
          </div>
        </Table.Cell>
      </Table.Row>
    );
  });
};
